import React, { useEffect, useRef } from 'react';

/**
 * 背景にふわふわした大きな水玉パーティクルを浮かべるコンポーネント。
 *
 * - 白に近いグレー（柔らかい乳白色）で控えめに発光
 * - radial gradient でエッジを柔らかく ぼんやり光る
 * - マウス（タッチ）に追従してやんわり避ける
 * - prefers-reduced-motion 対応：動きを止める
 */
export default function ParticleBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let animationId;
    const mouse = { x: -9999, y: -9999 };

    const isMobile = window.innerWidth < 720;
    const PARTICLE_COUNT = isMobile ? 5 : 8;
    const MOUSE_RADIUS = 220;

    // r: 超巨大ふわふわ。160〜400 px。
    const R_MIN = 160;
    const R_RANGE = 240;

    // ソフトなパステル系パレット。白い背景の上で柔らかく浮かぶ色たち。
    const PALETTE = [
      [160, 175, 190], // ライトグレー
      [170, 210, 180], // ソフトミントグリーン
      [170, 200, 230], // ソフトブルー
      [220, 200, 215], // ソフトピンク
      [230, 220, 180], // ソフトクリーム
      [200, 185, 220], // ソフトラベンダー
    ];

    let particles = [];
    let dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, 2));

    const resize = () => {
      dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, 2));
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const init = () => {
      resize();
      particles = Array.from({ length: PARTICLE_COUNT }, () => {
        // 基本ドリフト速度をさらにアップ (0.5 → 0.9)
        const dx = (Math.random() - 0.5) * 0.9;
        const dy = (Math.random() - 0.5) * 0.9;
        const color = PALETTE[Math.floor(Math.random() * PALETTE.length)];
        return {
          x: Math.random() * window.innerWidth,
          y: Math.random() * window.innerHeight,
          r: Math.random() * R_RANGE + R_MIN,
          dx,
          dy,
          baseDx: dx,
          baseDy: dy,
          // ダーク背景上でしっかり映えるパステル濃度: 0.70 〜 0.90。
          opacity: Math.random() * 0.20 + 0.70,
          pulse: Math.random() * Math.PI * 2,
          color,
          // 各パーティクルにふわふわ揺らぎパラメータ（速度・振幅アップ）
          swayPhaseX: Math.random() * Math.PI * 2,
          swayPhaseY: Math.random() * Math.PI * 2,
          swaySpeedX: 0.014 + Math.random() * 0.018, // 個体差
          swaySpeedY: 0.014 + Math.random() * 0.018,
          swayAmpX: 0.7 + Math.random() * 0.8, // 揺れ幅 0.7〜1.5
          swayAmpY: 0.7 + Math.random() * 0.8,
        };
      });
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (const p of particles) {
        if (!reduceMotion) {
          const diffX = p.x - mouse.x;
          const diffY = p.y - mouse.y;
          const dist = Math.sqrt(diffX * diffX + diffY * diffY);

          if (dist < MOUSE_RADIUS && dist > 0) {
            const force = (MOUSE_RADIUS - dist) / MOUSE_RADIUS;
            p.dx += (diffX / dist) * force * 0.4;
            p.dy += (diffY / dist) * force * 0.4;
          }

          // 元の速度に向けて減衰
          p.dx += (p.baseDx - p.dx) * 0.04;
          p.dy += (p.baseDy - p.dy) * 0.04;

          // 個体ごとのサイン揺らぎ（マウスに触れていなくても常に動く）
          p.swayPhaseX += p.swaySpeedX;
          p.swayPhaseY += p.swaySpeedY;
          const swayX = Math.sin(p.swayPhaseX) * p.swayAmpX;
          const swayY = Math.cos(p.swayPhaseY) * p.swayAmpY;

          p.x += p.dx + swayX;
          p.y += p.dy + swayY;
          p.pulse += 0.008;
        }

        if (p.x < -p.r) p.x = window.innerWidth + p.r;
        if (p.x > window.innerWidth + p.r) p.x = -p.r;
        if (p.y < -p.r) p.y = window.innerHeight + p.r;
        if (p.y > window.innerHeight + p.r) p.y = -p.r;

        const glow = Math.max(0, p.opacity + Math.sin(p.pulse) * 0.03);

        // パーティクルごとに割り当てたパステル色で radial gradient を描く
        const [cr, cg, cb] = p.color;
        const gradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r);
        gradient.addColorStop(0, `rgba(${cr}, ${cg}, ${cb}, ${glow})`);
        gradient.addColorStop(0.6, `rgba(${cr}, ${cg}, ${cb}, ${glow * 0.5})`);
        gradient.addColorStop(1, `rgba(${cr}, ${cg}, ${cb}, 0)`);

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      animationId = requestAnimationFrame(draw);
    };

    const onMouseMove = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };
    const onTouchMove = (e) => {
      if (e.touches[0]) {
        mouse.x = e.touches[0].clientX;
        mouse.y = e.touches[0].clientY;
      }
    };
    const onMouseLeave = () => {
      mouse.x = -9999;
      mouse.y = -9999;
    };

    init();
    draw();

    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('mouseleave', onMouseLeave);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('mouseleave', onMouseLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="particle-background"
      aria-hidden="true"
    />
  );
}
